import React from 'react';
import { api } from './api/hub';
import DailyHonors from './DailyHonors';

const TOKEN_KEY = 'hubToken';
const USER_KEY = 'hubUser';

const initialHubToken = () => {
  const queryToken = new URLSearchParams(window.location.search).get('hub_token');
  if (queryToken) {
    localStorage.setItem(TOKEN_KEY, queryToken);
    return queryToken;
  }
  return localStorage.getItem(TOKEN_KEY) || '';
};

const placeLabel = (place) => {
  const value = Number(place);
  if (!Number.isFinite(value)) return null;
  const suffix = value % 100 >= 11 && value % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[value % 10] || 'th');
  return `${value}${suffix}`;
};

function App() {
  const [user, setUser] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch (_) { return null; }
  });
  const [token, setToken] = React.useState(initialHubToken);
  const [games, setGames] = React.useState([]);
  const [yesterdayWinners, setYesterdayWinners] = React.useState({});
  const [gameStatuses, setGameStatuses] = React.useState({});
  const [todayResults, setTodayResults] = React.useState({});
  const [todayLeaders, setTodayLeaders] = React.useState({});
  const [dailyHonors, setDailyHonors] = React.useState([]);
  const [honorsDate, setHonorsDate] = React.useState('');
  const [dismissedHonors, setDismissedHonors] = React.useState('');
  const [authMode, setAuthMode] = React.useState('login');
  const [email, setEmail] = React.useState('');
  const [identifier, setIdentifier] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [resetToken, setResetToken] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const cleanHubTokenFromUrl = () => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('hub_token')) return;
      url.searchParams.delete('hub_token');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    };
    cleanHubTokenFromUrl();
    const applyContext = (contextData) => {
      if (!active) return;
      setGameStatuses(contextData.games || {});
      setYesterdayWinners(contextData.yesterday_winners || {});
      setTodayLeaders(contextData.today_leaders || {});
      setDailyHonors(contextData.yesterday_honors || []);
      setHonorsDate(contextData.yesterday_date || '');
      setTodayResults(Object.fromEntries(
        Object.entries(contextData.games || {})
          .filter(([, game]) => game.result_summary?.display_value)
          .map(([key, game]) => [key, {
            value: game.result_summary.display_value,
            place: game.result_summary.place,
          }]),
      ));
    };
    let refreshingContext = false;
    const refreshContext = () => {
      if (token && !refreshingContext && document.visibilityState !== 'hidden') {
        refreshingContext = true;
        api.gameContext(token).then(applyContext).catch(() => {})
          .finally(() => { refreshingContext = false; });
      }
    };
    api.games().then((data) => { if (active) setGames(data.games || []); })
      .catch((requestError) => { if (active) setError(requestError.message); });
    if (token) refreshContext();
    else applyContext({ games: {} });
    window.addEventListener('focus', refreshContext);
    window.addEventListener('pageshow', refreshContext);
    document.addEventListener('visibilitychange', refreshContext);
    if (token) {
      api.me(token).then((data) => {
        if (active) {
          setUser(data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        }
      }).catch(() => {
        api.exchangeLegacy(token).then((data) => {
          if (active) {
            localStorage.setItem(TOKEN_KEY, data.token);
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            setToken(data.token);
            setUser(data.user);
          }
        }).catch(() => {
          if (active) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setToken('');
            setUser(null);
          }
        });
      });
    }
    return () => {
      active = false;
      window.removeEventListener('focus', refreshContext);
      window.removeEventListener('pageshow', refreshContext);
      document.removeEventListener('visibilitychange', refreshContext);
    };
  }, [token]);

  const saveAuth = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
  };

  const submitAuth = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setMessage('');
    try {
      const data = authMode === 'signup'
        ? await api.signup(email, displayName, password)
        : await api.login(identifier, password);
      saveAuth(data);
      if (data.verification_token) setMessage(`Verification token for local development: ${data.verification_token}`);
      setPassword('');
    } catch (requestError) {
      setError(authMode === 'login'
        ? `${requestError.message} If you do not already have an account, please create one first.`
        : requestError.message);
    }
    finally { setBusy(false); }
  };

  const signOut = async () => {
    try { if (token) await api.logout(token); } catch (_) {}
    localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY);
    setToken(''); setUser(null); setMessage('');
  };

  const launchUrl = (url) => {
    const target = new URL(url, window.location.origin);
    const localTarget = ['localhost', '127.0.0.1'].includes(target.hostname);
    const localHub = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (localTarget && !localHub) target.hostname = window.location.hostname;
    target.searchParams.set('hub_token', token);
    target.searchParams.set('player_name', user?.display_name || '');
    return target.toString();
  };

  const honorsKey = user && honorsDate ? `${user.user_id || user.display_name}:${honorsDate}` : '';
  const honorsStorageKey = user ? `hubHonorsSeen:${user.user_id || user.display_name}` : '';
  const showHonors = user && games.length > 0 && dailyHonors.length > 0 && honorsKey
    && dismissedHonors !== honorsKey && localStorage.getItem(honorsStorageKey) !== honorsDate;
  const dismissHonors = () => {
    localStorage.setItem(honorsStorageKey, honorsDate);
    setDismissedHonors(honorsKey);
  };

  return (
    <main className="hub-shell">
      <header><h1>PrecisionPros Games</h1><p>One account for every game.</p></header>
      {user ? (
        <>
          <section className="hub-user"><span>Signed in as <strong>{user.display_name}</strong></span><button type="button" onClick={signOut}>Sign out</button></section>
          <section className="hub-card" aria-label="Games">
            {games.map((game) => <article key={game.key} className="game-tile" style={{ '--game-accent': game.accent, '--game-badge-text': game.badge_text_color || '#fff' }}>
              <div className="game-tile-heading">
                <span className="game-tile-badge">{game.badge}</span>
                <strong>
                  {game.wordmark_main ? <>
                    <span>{game.wordmark_main}</span><span className="game-wordmark-accent">{game.wordmark_accent}</span>{game.wordmark_suffix && <span>{game.wordmark_suffix}</span>}
                  </> : game.name}
                </strong>
              </div>
              <p>{game.description}</p>
              {yesterdayWinners[game.key] && <p className="game-tile-winner">Yesterday's {yesterdayWinners[game.key].winners?.length > 1 ? 'winners (tie)' : 'winner'}: {yesterdayWinners[game.key].name}{yesterdayWinners[game.key].value ? `: ${yesterdayWinners[game.key].value}` : ''}</p>}
              {(gameStatuses[game.key]?.played || todayResults[game.key]?.value) && todayLeaders[game.key] && <p className="game-tile-winner">Today's {todayLeaders[game.key].winners?.length > 1 ? 'leaders (tie)' : 'leader'}: {todayLeaders[game.key].name}{todayLeaders[game.key].value ? `: ${todayLeaders[game.key].value}` : ''}</p>}
              <div className="game-tile-actions">
                {gameStatuses[game.key]?.played || todayResults[game.key]?.value
                  ? <div className="game-action game-action-primary game-action-status">{todayResults[game.key]?.value ? (placeLabel(todayResults[game.key].place) ? `You are in ${placeLabel(todayResults[game.key].place)} place today: ${todayResults[game.key].value}` : `Today's result: ${todayResults[game.key].value}`) : "Today's result unavailable"}</div>
                  : <a className="game-action game-action-primary" href={launchUrl(game.url)}>Play</a>}
                {game.how_to_url && <a className="game-action" href={launchUrl(game.how_to_url)}>How To Play</a>}
                {game.leaderboard_url && <a className="game-action" href={launchUrl(game.leaderboard_url)}>Leaderboard</a>}
              </div>
            </article>)}
          </section>
        </>
      ) : authMode !== 'reset' ? (
        <section className="hub-card auth-card">
          <div className="auth-heading">
            <p className="auth-eyebrow">Your PrecisionPros Games account</p>
            <h2>{authMode === 'signup' ? 'Create your account' : 'Welcome back'}</h2>
            <p>{authMode === 'signup' ? 'Create one account to play all of our games.' : 'Sign in to continue to your games.'}</p>
          </div>
          <div className="auth-tabs" aria-label="Account access">
            <button type="button" className={authMode === 'login' ? 'active' : ''} aria-pressed={authMode === 'login'} onClick={() => { setAuthMode('login'); setError(''); }}>Sign in</button>
            <button type="button" className={authMode === 'signup' ? 'active' : ''} aria-pressed={authMode === 'signup'} onClick={() => { setAuthMode('signup'); setError(''); }}>Create account</button>
          </div>
          <form onSubmit={submitAuth}>
            {authMode === 'signup' && <><label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label>Display name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label></>}
            {authMode === 'login' && <label>Email or display name<input value={identifier} onChange={(event) => setIdentifier(event.target.value)} required /></label>}
            <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={authMode === 'signup' ? 8 : undefined} required /></label>
            <button className="primary-button" disabled={busy}>{busy ? 'Working…' : authMode === 'signup' ? 'Create account' : 'Sign in'}</button>
          </form>
          <button type="button" className="text-button" onClick={() => setAuthMode('reset')}>Forgot password?</button>
        </section>
      ) : null}
      {authMode === 'reset' && !user && <section className="hub-card reset-card"><h2>Reset password</h2><label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label><button className="primary-button" onClick={async () => { try { const data = await api.requestPasswordReset(email); setMessage(data.reset_token ? `Reset token for local development: ${data.reset_token}` : data.message); if (data.reset_token) setResetToken(data.reset_token); } catch (requestError) { setError(requestError.message); } }}>Request reset</button>{resetToken && <><label>Reset token<input value={resetToken} onChange={(event) => setResetToken(event.target.value)} /></label><label>New password<input type="password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} /></label><button className="primary-button" onClick={async () => { try { await api.resetPassword(resetToken, password); setAuthMode('login'); setMessage('Password updated. Sign in with the new password.'); } catch (requestError) { setError(requestError.message); } }}>Set password</button></>}</section>}
      {message && <p className="hub-message">{message}</p>}
      {error && <p className="hub-error" role="alert">{error}</p>}
      {showHonors && <DailyHonors honors={dailyHonors} games={games} viewerName={user.display_name} onDismiss={dismissHonors} />}
    </main>
  );
}

export default App;
